import {HttpClient} from '@angular/common/http';
import {Injectable} from '@angular/core';
import {Result} from '@domain/common/http/result';
import {Geographie} from "@domain/geolocalisation/geographie";
import {Localisation} from "@domain/geolocalisation/localisation";
import {SaveAdresseResult} from "@domain/geolocalisation/saveAdresseResult";
import {Adresse} from "@domain/profil/adresse";
import {environment} from '@environments/environment';
import {Observable} from 'rxjs';
import {first,map} from 'rxjs/operators';
import {LieuParam} from "@domain/common/lieu/lieu-param";

/**
 * Service de gestion des lieux OD
 */
@Injectable()
export class LieuService {
    /**
     * Constructeur
     */
    constructor(private http: HttpClient) {}

    /**
     * Chargement des adresses
     */
    loadAdresses(lieuParam: LieuParam): Observable<any> {
        //Chargement du chainage
        return this.http.post<Result>(lieuParam.urlAdresse,null).pipe(
            first(),
            map(result => result.data)
        );
    }

    /**
     * Interrogation du controller de géolocalisation
     *
     * @param geolocalisation Objet contenant les données nécéssaires à la géolocalisation
     */
    getListeLocalisation(geolocalisation: Localisation): Observable<Array<Localisation>> {
        //Définition de l'url suivant la portée
        const url = `${environment.baseUrl}/controller/Geolocalisation/getListeLocalisation`;

        //Interrogation et Récupération du résultat
        return this.http.post<Result>(url,geolocalisation).pipe(
            first(),
            map(result => result.data.listeGeolocalisation)
        );
    }

    /**
     * Enregistrement d'une adresse à partir d'une géolocalisation
     *
     * @param geolocalisation Adresse géolocalisée à enregistrer en base
     */
    saveAdresse(geolocalisation: Adresse): Observable<SaveAdresseResult> {
        let result: SaveAdresseResult;

        //Enregistrement de l'adresse pour la géolocalisation en paramètre
        return this.http.put<Result>(`${environment.baseUrl}/controller/Adresse/saveAdresse`,geolocalisation).pipe(
            first(),
            map(data => {
                //Construction de l'objet de retour contenant l'adresse et l'entité géographique associée
                result = {
                    adresse: new Adresse({
                        idAdresse: data.data?.idAdresse,
                        rue: data.data?.rue,
                        ville: data.data?.ville,
                        codePostal: data.data?.codePostal,
                        pays: data.data?.pays,
                        codePays: data.data?.codePays,
                    }),
                    geographie: this.parseGeographie(data.data?.geographie)
                }

                return result;
            })
        );
    }

    /**
     * Construit un objet Geographie à partir des data passées en paramètre
     *
     * @param data retournées par le controller
     */
    parseGeographie(data: any): Geographie {
        return {
            id: data.id,
            type: data.type,
            code: data.code,
            libelle: data.libelle,
            entiteGeo: JSON.parse(data.entiteGeoJSON)
        };
    }

    /**
     * Retourne la concaténation des différents champs constituant une adresse à partir des champs d'un objet de type Localisation.
     *
     * @param localisation Objet localisation
     */
    getAdresseFromLocalisation(localisation: Localisation): string {
        return [(localisation.rue ? localisation.rue : null),
                (localisation.codePostal || localisation.ville ? ((localisation.codePostal || '').trimRight() + ' ' + (localisation.ville || '').trimLeft()).trim() : null),
                (localisation.pays ? localisation.pays : null)
        ].filter(v => !!v).join(", ");
    }

    /**
     * Format l'adresse en string
     *
     * @param adresse Objet adresse qui sera formatée
     * @return le string formaté de l'adresse
     */
    formatAdressetoString(adresse: Adresse): string {
        const codeVille = [adresse?.codePostal, adresse?.ville].filter(v => !!v).join(' ');
        return [adresse?.rue, codeVille, adresse?.pays].filter(v => !!v && v.trim() !== '').join(', ');
    }

    /**
     * Vérifie si des informations obligatoires sont manquantes sur une adresse
     *
     * @param adresse Adresse à vérifier
     */
    isAdresseIncomplete(adresse: Adresse): boolean {
        return !adresse.rue || !adresse.ville || !adresse.pays;
    }

}
